/**
 * @file useProductRowActions.ts
 * @description Меню «⋯» строки реестра товаров (phase19.31, макет (а)):
 * «Открыть», «Опубликовать» / «Снять с публикации», после линии — «В архив»
 * (опасное, с подтверждением окном кабинета, а не системным `confirm`).
 *
 * После действия список перечитывается целиком (один источник правды:
 * остатки, публикация и счётчики пилюль считает сервер).
 */

import { Archive, Eye, EyeOff, FolderOpen } from "lucide-react";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import { useNotify } from "../../ui/NotifyProvider";
import type { RowAction } from "../../ui/RowActionsMenu";
import { useConfirm } from "../../ui/use-confirm";
import { catalogProducts, type CatalogProduct } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";

export function useProductRowActions(productHref: (p: CatalogProduct) => string, onChanged: () => void) {
  const navigate = useNavigate();
  const notify = useNotify();
  const confirmApi = useConfirm();
  const { confirm } = confirmApi;

  const run = useCallback(
    async (work: () => Promise<unknown>, done: string, failed: string) => {
      try {
        await work();
        notify.success(done);
        onChanged();
      } catch (err) {
        notify.error(err instanceof ApiError ? err.message : failed);
      }
    },
    [notify, onChanged],
  );

  const rowActions = useCallback(
    (p: CatalogProduct): RowAction[] => {
      const actions: RowAction[] = [{ key: "open", label: "Открыть", icon: FolderOpen, run: () => navigate(productHref(p)) }];
      if (p.status !== "ARCHIVED") {
        const publish = p.status !== "ACTIVE";
        actions.push({
          key: "publish",
          label: publish ? "Опубликовать" : "Снять с публикации",
          icon: publish ? Eye : EyeOff,
          run: () =>
            void run(
              () => catalogProducts.updateStatus(p.id, publish ? "ACTIVE" : "DRAFT"),
              publish ? "Товар опубликован" : "Товар снят с публикации",
              "Не удалось изменить публикацию",
            ),
        });
        actions.push({
          key: "archive",
          label: "В архив",
          icon: Archive,
          danger: true,
          run: () =>
            void (async () => {
              const ok = await confirm({
                title: `Отправить «${p.displayName}» в архив?`,
                description: "Товар пропадёт с сайта и из новых заказов. История продаж и движений сохранится.",
                action: "В архив",
                danger: true,
              });
              if (ok) await run(() => catalogProducts.archive(p.id), "Товар отправлен в архив", "Не удалось отправить в архив");
            })(),
        });
      }
      return actions;
    },
    [confirm, navigate, productHref, run],
  );

  return { rowActions, confirmRequest: confirmApi.request, settleConfirm: confirmApi.settle };
}
